import React, {createRef, useState} from 'react';
import Draggable from "react-draggable";
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import {TablePagination, Select, MenuItem, Dialog, DialogTitle, DialogContent, Paper, useTheme} from "@material-ui/core";
import PreviewIcon from '@mui/icons-material/Preview';

import AirportLookup from "../../Airports/components/AirportsLookup";

import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import AircraftScheduledMaintenanceValidator from "../validators/AircraftScheduledMaintenanceValidator";
import { useScheduledMaintenance, useAddScheduledMaintenance, useUpdateScheduledMaintenance, useDeleteScheduledMaintenance } from "../../../services/aircraft";
import { MaskedText, DatePicker, MaskedTime, ComboSelect } from "../../../controls";
import AogActivity from "./AogActivity";

const AccessControl = require("../../../AccessControl");
const dateFormat = "DD/MM/YYYY";
const today = moment.utc().add(-1, 'days').toISOString();
const tomorrow = moment.utc().add(1,'day').toISOString();

const getUserName = () => {
  const userInfo = AccessControl.getUserInfo();
  if (userInfo && "firstName" in userInfo && "lastName" in userInfo)
    return (userInfo.firstName + " " + userInfo.lastName);
  else
    return "";
}

const getMomentFromComponents = (datePart, timePart) => {
  const startDate = moment.utc(datePart).format("YYYY-MM-DD ") + timePart;
  return moment.utc(startDate, "YYYY-MM-DD HH:mm");
}

const setDateFromComponents = (datePart, timePart) => {
  return getMomentFromComponents(datePart, timePart).format();
}

const PaperComponent = (props) => {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const AircraftScheduledMaintenanceList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(AircraftScheduledMaintenanceValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    currentUser: getUserName(),
    maintenanceInterface: props.maintenanceInterface
  });
  const [preview, setPreview] = useState({open: false, data: null});

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddScheduledMaintenance = useAddScheduledMaintenance();
  const handleUpdateAircraftRate = useUpdateScheduledMaintenance();
  const handleDeleteScheduledMaintenance = useDeleteScheduledMaintenance();

  const queryClient = useQueryClient();
  const extraKeys = [props.aircraftTailNumber, props.period, 'AircraftMaintenance'];
  const maintenanceSchedule = useScheduledMaintenance({tableRef, queryClient, extraKeys});

  if (maintenanceSchedule && maintenanceSchedule.data) {
    maintenanceSchedule.data.forEach(record => {
      record["startTime"] = moment.utc(record.startCheckDate).format("HH:mm");
      record["endTime"] = moment.utc(record.endCheckDate).format("HH:mm");
    })
  }

  const handlePreview = (rowData) => {
    setPreview({open: true, data: rowData});
  }

  const closePreview = () => {
    setPreview({open: false, data: null})
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const statusValues = [
      {code:"REQUESTED",value:"Requested", aog: false, check: true},
      {code:"CONFIRMED",value:"Confirmed", aog: true, check: true},
      {code:"NEGOTIATING",value:"Negotiating", aog: true, check: true},
      {code:"RECOVERED",value:"Recovered", aog: true, check: false},
  ];

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        //...(ColumnWidth(140)),
        ...(ColumnWidth(140, undefined, undefined, {paddingLeft: 12, paddingTop: 8}, {paddingLeft: 12})),
        title: "Check / AOG",
        field: "aircraftCheck",
        editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        render: (rowData) => (
          <ComboSelect
            id="aircraftCheck"
            group="MAINTENANCE CODES"
            subGroup={state.maintenanceInterface ? "AOG" : "ACTIVE"}
            fullWidth
            value={rowData.aircraftCheck}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="aircraftCheck"
            group="MAINTENANCE CODES"
            subGroup={state.maintenanceInterface ? "AOG" : "ACTIVE"}
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 2, paddingTop: 10}, {paddingLeft: 2})),
        title: "Scheduled Start",
        field: "startCheckDate",
        type: "date",
        editable: ((cell,rowData,updatedData) => {
          if (!props.canEdit) return false;
          if (!state.maintenanceInterface) return true;
          if (updatedData.aircraftCheck === 'AOG') return true;
          return false;
        }),
        // editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        editComponent: (props) => (
          <DatePicker
            minDate={props.rowData.aircraftCheck === "AOG" ? today : tomorrow}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100, undefined, undefined, {paddingLeft: 2, paddingTop: 9}, {paddingLeft: 2})),
        title: "Start Time",
        field: "startTime",
        editable: ((cell,rowData,updatedData) => {
          if (!props.canEdit) return false;
          if (!state.maintenanceInterface) return true;
          if (updatedData.aircraftCheck === 'AOG') return true;
          return false;
        }),
        //editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (<span>{rowData.startTime}</span>),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 2, paddingTop: 10}, {paddingLeft: 2})),
        title: "Scheduled End",
        field: "endCheckDate",
        type: "date",
        editable: ((cell,rowData,updatedData) => {
          if (!props.canEdit) return false;
          if (!state.maintenanceInterface) return true;
          if (updatedData.aircraftCheck === 'AOG') return true;
          return false;
        }),
        //editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        editComponent: (props) => (
          <DatePicker
            minDate={props.rowData.startCheckDate}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100, undefined, undefined, {paddingLeft: 2, paddingTop: 9}, {paddingLeft: 2})),
        title: "End Time",
        field: "endTime",
        editable: ((cell,rowData,updatedData) => {
          if (!props.canEdit) return false;
          if (!state.maintenanceInterface) return true;
          if (updatedData.aircraftCheck === 'AOG') return true;
          return false;
        }),
        //editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (<span>{rowData.endTime}</span>),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(250, undefined, undefined, {paddingLeft: 2, paddingTop: 9}, {paddingLeft: 2})),
        cellStyle: { padding: "11px 2px 8px 2px" },
        headerStyle: { padding: "11px 2px 8px 2px" },
        title: "Check / AOG Info",
        field: "mxAircraftCheckInfo",
        editable: ((cell,rowData,updatedData) => {
          if (!props.canEdit) return false;
          if (!state.maintenanceInterface) return true;
          if (updatedData.aircraftCheck === 'AOG') return true;
          return false;
        }),
        //editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140, undefined, undefined, {paddingLeft: 2, paddingTop: 11}, {paddingLeft: 2})),
        title: "At Airport",
        field: "mxAirportCode",
        editable: ((cell,rowData,updatedData) => {
          if (!props.canEdit) return false;
          if (!state.maintenanceInterface) return true;
          if (updatedData.aircraftCheck === 'AOG') return true;
          return false;
        }),
        //editable: props.canEdit ? `${!state.maintenanceInterface ? "always" : "onAdd"}` : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        cellStyle: { padding: "11px 2px 8px 2px" },
        headerStyle: { padding: "11px 2px 8px 2px" },
        title: "Duration",
        field: "startCheckDate",
        emptyValue: null,
        initialEditValue: null,
        editable: "never",
        render: (rowData,  rowType, updatedData) => {
          const end = getMomentFromComponents(updatedData.endCheckDate, updatedData.endTime);
          const start = getMomentFromComponents(updatedData.startCheckDate, updatedData.startTime);
          const duration = moment.duration(end.diff(start, 'hours', true), 'hours');
          const days = Math.floor(duration?.asDays());
          const hours = Math.floor(duration?.asHours() % 24);
          const displayDuration = `${days && days !== 0 ? `${days} days` : ""}${days && days !== 0 && hours && hours !== 0 ? ", ": ""}${hours && hours !== 0 ? `${hours} hours` : ""}`;
          return (<span>{displayDuration}</span>
          )}
      },
      {
        ...ColumnWidth(25),
        ...baseColumn,
      },
      {
        ...baseColumn,
        ...(ColumnWidth(undefined, undefined, undefined, {paddingLeft: 2, paddingTop: 9}, {paddingLeft: 2})),
        title: "Notes",
        field: "notes",
        initialEditValue: "",
        editable: "always",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140, undefined, undefined, {paddingLeft: 2, paddingTop: 9}, {paddingLeft: 2})),
        title: "Status",
        field: "status",
        editable: "always",
        render: rowData => {
          const value = (<div>{ __.find(statusValues, {code: rowData.status||"REQUESTED"}).value}</div>);
          return value;
        },
        editComponent: (props) => {
          return (
            <Select
              value={props.value}
              onChange={
                (evt, data) => {
                  const statusValue = __.find(statusValues, {value: data.props.value || "Requested"});
                  props.onChange(statusValue.code);
                }
              }
              renderValue={(value) => {
                const statusValue = __.find(statusValues, {code: value || "REQUESTED"});
                const valueToRender = (statusValue === undefined) ? "" : statusValue.value;
                return(`${valueToRender}`);
              }}
            >
              {(statusValues) ?
                statusValues.map((record) => {
                  if (props.rowData.aircraftCheck === "AOG") {
                    if (record.aog)
                      return (<MenuItem key={record.code} value={record.value}>{record.value}</MenuItem>);
                  } else {
                    if (record.check)
                      return (<MenuItem key={record.code} value={record.value}>{record.value}</MenuItem>);
                  }
                }) :
                null
              }
            </Select>
          )
        }
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{marginLeft: 50}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        initialFormData={{startCheckDate: tomorrow, endCheckDate: tomorrow, startTime: '00:00', endTime: '00:00', aircraftCheck: '', status: ''}}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
          addRowPosition: "first",
        }}
        actions={[
          (rowData) => (
            {
              disabled: rowData.aircraftCheck !== 'AOG',
              hidden: false,
              icon: () => ( <PreviewIcon   onClick={()=>{handlePreview(rowData)}}/>),
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              tooltip: "View AOG details"
            }
          )
        ]}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        // actions={[
        //   (rowData) => (
        //     {
        //       hidden: false,
        //       disabled: false,
        //       tooltip: "Confirm Maintenance",
        //       icon: () => (
        //         <IconButton variant="contained" color="primary" size={"small"}>
        //           <LibraryAddCheck/>
        //         </IconButton>),
        //       iconProps: { style: { color: '#fc7c35' }},
        //       isFreeAction: false,
        //       onClick: (e, rowData) => {
        //         //perform the action;
        //       }
        //     }
        //   ),
        // ]}
        title="Scheduled Maintenance"
        columns={getColumns({canEdit: state.canEdit})}
        data={maintenanceSchedule.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              newData.startCheckDate = setDateFromComponents(newData.startCheckDate, newData.startTime);
              newData.endCheckDate = setDateFromComponents(newData.endCheckDate, newData.endTime);
              handleAddScheduledMaintenance.mutate({record: {...newData, aircraftReg: props.aircraftTailNumber}, resolve, reject, extraKeys});
            })
            : undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              newData.startCheckDate = setDateFromComponents(newData.startCheckDate, newData.startTime);
              newData.endCheckDate = setDateFromComponents(newData.endCheckDate, newData.endTime);
              handleUpdateAircraftRate.mutate({record: {...newData}, oldRecord: oldData, resolve, reject, extraKeys});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteScheduledMaintenance.mutate({oldRecord: {...oldData}, resolve, reject, extraKeys});
            }),
        }}
      />
      {preview.open
        ?
        <Dialog
          open={preview.open}
          onClose={closePreview}
          maxWidth="md" fullWidth
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
        >
          <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title" />
          <DialogContent>
            {props.width && <div style={{width: props.width, minWidth: props.width, height: 1}}>&nbsp;</div>}
            <AogActivity activity={preview?.data?.aogActivities} maxHeight={`calc(100vh - 100px)`}/>
          </DialogContent>
        </Dialog>
        :
        <></>
      }
    </div>
  );

}

export default AircraftScheduledMaintenanceList;
